// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { Clickable } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import CaretLeft from "@werner/XDL/Icons/CaretLeft";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        contentPadding: string;
        customCSS: string;
    };
}

const enum fields {
    carouselHeader = "carouselHeader",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.carouselHeader]: string;
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
    };
}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

// styles in Extensions folder
export const carouselStyles: CarouselStyles = {};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}
`;

const styles = carouselStyles;

export const Carousel = ({ fields, id }: OwnProps) => {
    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <>
            {fields.carouselHeader && (
                <Typography {...styles.titleText}>{parse(fields.carouselHeader, parserOptions)}</Typography>
            )}
            <StyledWrapper {...customCssWrapper}>
                <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                    {fields.showArrows && fields.slides.length > 1 && (
                        <StyledWrapper
                            {...styles.prevArrowWrapper}
                            onClick={() => embla && embla.scrollPrev()}
                            className="slide-arrow-wrapper"
                        >
                            <Clickable {...styles.prevArrowButton}>
                                <ButtonIcon {...styles.iconProps} src={CaretLeft} />
                            </Clickable>
                        </StyledWrapper>
                    )}
                    <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                            {fields.slides.map((slide, index) => {
                                const slideOverlayWrapperStyles = {
                                    css: css`
                                        ${styles.slideOverlayWrapper?.css || ""}
                                        padding: ${slide.fields.contentPadding};
                                    `,
                                };
                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <StyledWrapper
                                        className="slide-wrapper"
                                        key={index}
                                        {...styles.slideContentWrapper}
                                    >
                                        <StyledWrapper {...slideOverlayWrapperStyles}>
                                            <XngZone zoneName={`SlideZone${index}`} contentId={id} hint="Slide Zone" />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                    {fields.slideIndicator && (
                        <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                            {fields.slides.map((_, i) => {
                                return (
                                    <Clickable
                                        className="slide-dot-button"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={i}
                                        {...styles.dotButton}
                                        onClick={() => embla && embla.scrollTo(i)}
                                        aria-selected={i === selectedIndex}
                                    >
                                        <StyledWrapper />
                                    </Clickable>
                                );
                            })}
                        </StyledWrapper>
                    )}
                    {fields.showArrows && fields.slides.length > 1 && (
                        <StyledWrapper
                            className="slide-arrow-wrapper"
                            {...styles.nextArrowWrapper}
                            onClick={() => embla && embla.scrollNext()}
                        >
                            <Clickable className="slide-arrow" {...styles.nextArrowButton}>
                                <ButtonIcon {...styles.iconProps} src={CaretRight} />
                            </Clickable>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Carousel,
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.carouselHeader,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tab: contentTab,
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Enter: 5px or any padding style",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
